// Admin › Settings › Referral Program › Ledger — reward credit movements (part 4).
// The newest grants and take-backs across all wallets, with what is left of
// each grant and when it expires.

import { useQuery } from '@tanstack/react-query'
import { fetchRewardsLedger, ledgerKey } from '../../../api/referralRewards'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import { useT } from '../../../i18n'
import { rewardsLedgerI18n } from '../../../i18n/admin/rewards-referrals'
import { formatCurrency } from '../../../lib/currency'
import { formatDate } from '../../../lib/datetime'

function fmtDate(iso: string): string {
  return formatDate(iso, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function RewardsLedgerPage() {
  const t = useT(rewardsLedgerI18n)
  const query = useQuery({ queryKey: ledgerKey, queryFn: fetchRewardsLedger })
  const rows = query.data?.items ?? []

  return (
    <Card className="space-y-3 p-4">
      <div>
        <h2 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h2>
        <p className="mt-0.5 text-sm text-[var(--color-text-muted)]">{t.subtitle}</p>
      </div>
      {query.isLoading ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t.loading}</p>
      ) : query.isError ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span role="alert" className="text-[var(--color-danger)]">
            {t.loadError}
          </span>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t.retry}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t.empty}</p>
      ) : (
        <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th>{t.columns.date}</th>
                <th>{t.columns.seller}</th>
                <th>{t.columns.movement}</th>
                <th className="text-right">{t.columns.amount}</th>
                <th>{t.columns.expires}</th>
                <th className="text-right">{t.columns.left}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.wallet_event_id}>
                  <td className="tabular-nums">{fmtDate(row.created_at)}</td>
                  <td className="font-medium">{row.org_name}</td>
                  <td>
                    <Badge variant={row.kind === 'granted' ? 'success' : 'warning'}>
                      {t.kinds[row.kind]}
                    </Badge>
                    {row.source && (
                      <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                        {t.sources[row.source] ?? row.source}
                      </span>
                    )}
                  </td>
                  <td className="text-right tabular-nums">{formatCurrency(Number(row.amount_usd), 'USD')}</td>
                  <td className="tabular-nums">
                    {row.kind === 'granted' ? (row.expires_at ? fmtDate(row.expires_at) : t.never) : '—'}
                  </td>
                  <td className="text-right tabular-nums">
                    {row.remaining_usd == null ? '—' : formatCurrency(Number(row.remaining_usd), 'USD')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>
      )}
    </Card>
  )
}
